import { useRef, useState } from 'react';
import { Button, Col, Modal, Progress, Row, Select, Space, Typography, Upload, message } from 'antd';
import { CloseCircleOutlined, FileTextOutlined, InboxOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useS3Upload } from 'lib/s3';

const { Dragger } = Upload;
const { Text } = Typography;

const MAX_FILES = 50;
const ALLOWED_TYPES = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

const StyledModal = styled(Modal)`
  .ant-modal-header {
    background: #000;
  }
`;

const StyledDragger = styled(Dragger)`
  background: #0f1c2e !important;
  border: 2px dashed #2f3c4d !important;
  border-radius: 16px !important;
  padding: 40px 0;

  .ant-upload-text {
    color: #9aa4af;
    font-size: 20px;
  }

  .ant-upload-hint {
    color: #6c7a89;
  }
`;

export function BulkUploadModal({
  data,
  open,
  onClose,
  onConfirm,
  isLoading,
  title = 'อัปโหลดเอกสาร',
  module = 'online_scam',
  group = 'document',
  dragText = 'อัปโหลดไฟล์ CDR, CFR',
  dragHint = 'รองรับ .xlsx, .csv สูงสุด 50 ไฟล์',
  successMessage = 'อัปโหลดไฟล์สำเร็จ',
  errorMessage = 'ไม่สามารถอัปโหลดไฟล์นี้ได้ กรุณาลองใหม่อีกครั้ง',
}) {
  const [fileList, setFileList] = useState([]);
  const uploadCallbacksRef = useRef({});

  const { mutate: mutateUpload, isPending } = useS3Upload({
    onSuccess: (res) => {
      setFileList((prev) =>
        prev.map((ss) => {
          if (ss.uid === res?.uid) {
            return {
              ...ss,
              status: 'done',
              url: res?.url,
              name: res?.file_name,
              response: res,
              percent: 100,
            };
          }
          return ss;
        })
      );
      const callbacks = uploadCallbacksRef.current?.[res?.uid];
      if (callbacks?.onProgress) {
        callbacks.onProgress({ percent: 100 });
      }
      callbacks?.onSuccess?.(res, callbacks?.file);
      if (res?.uid) {
        delete uploadCallbacksRef.current[res.uid];
      }
      message.success(`${successMessage} (${res?.file_name})`);
    },
    onError: () => {
      setFileList((prev) =>
        prev.map((ss) => {
          if (ss.status === 'uploading') {
            return {
              ...ss,
              status: 'error',
            };
          }

          return ss;
        })
      );
      Object.values(uploadCallbacksRef.current).forEach((callbacks) => {
        callbacks?.onError?.(new Error('Upload failed'));
      });
      uploadCallbacksRef.current = {};
      message.error(errorMessage);
    },
  });

  const uploadToS3 = ({ file, onError, onProgress, onSuccess }) => {
    try {
      const actualFile = file.originFileObj || file;

      if (!actualFile) {
        onError?.(new Error('Missing file'));
        return;
      }

      onProgress?.({ percent: 0 });

      uploadCallbacksRef.current[file.uid] = {
        file,
        onError,
        onProgress,
        onSuccess,
      };

      mutateUpload({
        file: actualFile,
        module,
        group,
        uid: file.uid,
        onProgress: (percent) => {
          setFileList((prev) =>
            prev.map((ss) =>
              ss.uid === file.uid
                ? {
                    ...ss,
                    status: 'uploading',
                    percent,
                  }
                : ss
            )
          );

          const callbacks = uploadCallbacksRef.current?.[file.uid];
          callbacks?.onProgress?.({ percent });
        },
      });
    } catch (err) {
      onError?.(err);
      message.error('อัปโหลดล้มเหลว');
    }
  };

  const props = {
    multiple: true,
    accept: '.csv,.xlsx',
    fileList,
    customRequest: uploadToS3,
    beforeUpload: (file) => {
      const isValidType =
        ALLOWED_TYPES.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx');

      if (!isValidType) {
        console.error('อัปโหลดได้เฉพาะไฟล์ .csv และ .xlsx เท่านั้น');
        return Upload.LIST_IGNORE;
      }

      if (fileList.length >= MAX_FILES) {
        console.error(`อัปโหลดได้สูงสุด ${MAX_FILES} ไฟล์`);
        return Upload.LIST_IGNORE;
      }

      setFileList((prev) => [...prev, { ...file, status: 'uploading', percent: 0 }]);
    },
    onChange(info) {
      setFileList(info.fileList);
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    showUploadList: false,
  };

  const handleClose = () => {
    setFileList([]);
    onClose();
  };
  return (
    <StyledModal
      title={title}
      visible={open}
      onCancel={handleClose}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleClose} loading={isPending || isLoading}>
          ยกเลิก
        </Button>,
        <Button key="submit" type="primary" onClick={() => onConfirm?.(fileList)} loading={isPending || isLoading}>
          บันทึก
        </Button>,
      ]}
      width={900}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 20,
          width: '100%',
        }}
      >
        <Select style={{ minWidth: 300 }} defaultValue="CDR" placeholder="เลือกประเภทไฟล์" allowClear>
          <Select.Option value="CDR">Call Detail Records (CDRs)</Select.Option>
          <Select.Option value="CFR">Central Fraud Registry (CFR)</Select.Option>
        </Select>
      </div>
      <StyledDragger {...props}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ fontSize: 40, color: '#6c7a89' }} />
        </p>
        <p className="ant-upload-text">{dragText}</p>
        <p className="ant-upload-hint">{dragHint}</p>
      </StyledDragger>

      {fileList.map((file) => (
        <div key={file.uid}>
          <Row align="middle" justify="space-between" style={{ width: '100%', marginTop: 10 }}>
            <Col xs={1}>
              <FileTextOutlined style={{ fontSize: 40, color: file.status === 'error' ? '#FF0000' : '#22c55e' }} />
            </Col>
            <Col xs={21} style={{ width: '100%' }}>
              <Space direction="vertical" size={5} style={{ width: '100%' }}>
                <Text
                  style={{
                    color: file.status === 'error' ? '#FF0000' : undefined,
                  }}
                >
                  {file.name}
                </Text>
                {file.status !== 'uploading' ? (
                  <small
                    style={{
                      color: file.status === 'error' ? '#FF0000' : undefined,
                    }}
                  >
                    File size - {(file.size / 1024).toFixed(0)} KB{' '}
                    <span>{file.status === 'error' ? 'ผิดพลาด' : ''}</span>
                  </small>
                ) : (
                  <Progress
                    status="active"
                    style={{ width: '100%' }}
                    percent={file.percent || 0}
                    showInfo={false}
                    strokeColor="#4f6df5"
                  />
                )}
              </Space>
            </Col>
            <Col xs={1}>
              <CloseCircleOutlined
                style={{
                  fontSize: 20,
                  color: '#FF0000',
                }}
                className="remove"
                onClick={() => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
              />
            </Col>
          </Row>
        </div>
      ))}
    </StyledModal>
  );
}
